import { create } from '@bufbuild/protobuf'
import { timestampDate, timestampFromDate } from '@bufbuild/protobuf/wkt'
import { TZDate } from '@date-fns/tz'
import { format } from 'date-fns'

import type { EventPlanInputChange, EventTimelineBlock } from '@/gen/cortado/v1/event_plan_pb'
import { describeInputChange } from '@/lib/event-plan'
import { TimelineBlockKindEnum } from '@/gen/cortado/v1/event_plan_pb'
import { EventTimelineBlockInputSchema, type EventTimelineBlockInput } from '@/gen/cortado/v1/event_timeline_pb'

/** Limits the server enforces on an edited timeline (event_timeline.go). */
export const MAX_TIMELINE_BLOCKS = 24
export const MAX_TIMELINE_LABEL_LENGTH = 80
export const MAX_TIMELINE_BLOCK_MINUTES = 24 * 60

/** One editable row of a timeline being edited. */
export interface TimelineDraftBlock {
  /** Stable React key: the saved ID, or a local one for added rows. */
  key: string
  /** Saved block ID; unset for rows added in this edit. */
  id?: string
  kind: TimelineBlockKindEnum
  label: string
  start: Date
  minutes: number
  note: string
}

const KIND_COLORS: Record<TimelineBlockKindEnum, string> = {
  [TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_UNSPECIFIED]: 'bg-muted-foreground',
  [TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_PREP]: 'bg-chart-3',
  [TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_DRIVE_TO]: 'bg-chart-1',
  [TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_SETUP]: 'bg-chart-4',
  [TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_EVENT]: 'bg-chart-2',
  [TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_TEARDOWN]: 'bg-chart-4',
  [TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_DRIVE_BACK]: 'bg-chart-1',
  [TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_CUSTOM]: 'bg-muted-foreground',
}

/** The accent bar color for a block kind. */
export function timelineKindColor(kind: TimelineBlockKindEnum): string {
  return KIND_COLORS[kind] ?? KIND_COLORS[TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_CUSTOM]
}

export function isDriveKind(kind: TimelineBlockKindEnum): boolean {
  return (
    kind === TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_DRIVE_TO ||
    kind === TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_DRIVE_BACK
  )
}

/** "45 mins", "2 hrs", "2 hrs 30 mins". */
export function formatBlockDuration(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  const hrs = `${h} hr${h !== 1 ? 's' : ''}`
  const mins = `${m} min${m !== 1 ? 's' : ''}`
  if (h > 0 && m > 0) return `${hrs} ${mins}`
  if (h > 0) return hrs
  return mins
}

function zoned(date: Date, timeZone?: string): Date {
  return timeZone ? new TZDate(date.getTime(), timeZone) : date
}

/** "8:30 AM" in the timeline's zone. */
export function formatBlockTime(date: Date, timeZone?: string): string {
  return format(zoned(date, timeZone), 'h:mm a')
}

/** "HH:mm" for a time input, in the timeline's zone. */
export function blockTimeInputValue(date: Date, timeZone?: string): string {
  return format(zoned(date, timeZone), 'HH:mm')
}

/**
 * Moves `date` to the wall-clock time `value` ("HH:mm") on the same local
 * day in the timeline's zone. An unparsable value returns `date` unchanged.
 */
export function withBlockTime(date: Date, value: string, timeZone?: string): Date {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value)
  if (!match) return date
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return date
  const local = zoned(date, timeZone)
  if (timeZone) {
    return new Date(
      new TZDate(local.getFullYear(), local.getMonth(), local.getDate(), hours, minutes, 0, 0, timeZone).getTime(),
    )
  }
  const next = new Date(date)
  next.setHours(hours, minutes, 0, 0)
  return next
}

/** Editable rows from saved blocks. */
export function draftFromBlocks(blocks: EventTimelineBlock[]): TimelineDraftBlock[] {
  return blocks.map((b, i) => ({
    key: b.id || `saved-${i}`,
    id: b.id || undefined,
    kind: b.kind,
    label: b.label,
    start: b.start ? timestampDate(b.start) : new Date(0),
    minutes: b.minutes,
    note: b.note,
  }))
}

/** A new custom row starting where the last row ends (15 minutes long). */
export function newDraftBlock(draft: TimelineDraftBlock[], key: string, fallbackStart: Date): TimelineDraftBlock {
  const last = draft[draft.length - 1]
  const start = last ? new Date(last.start.getTime() + last.minutes * 60_000) : fallbackStart
  return {
    key,
    kind: TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_CUSTOM,
    label: '',
    start,
    minutes: 15,
    note: '',
  }
}

/** Moves the row at `index` by `delta` (-1 up, +1 down). Out of range is a no-op. */
export function moveDraftBlock(draft: TimelineDraftBlock[], index: number, delta: number): TimelineDraftBlock[] {
  const target = index + delta
  if (index < 0 || index >= draft.length || target < 0 || target >= draft.length) return draft
  const next = [...draft]
  const [row] = next.splice(index, 1)
  next.splice(target, 0, row)
  return next
}

/** The first problem with a draft, worded for the user, or null when it can be saved. */
export function draftProblem(draft: TimelineDraftBlock[]): string | null {
  if (draft.length === 0) return 'Keep at least one block.'
  if (draft.length > MAX_TIMELINE_BLOCKS) return `A timeline can have at most ${MAX_TIMELINE_BLOCKS} blocks.`
  for (const [i, b] of draft.entries()) {
    if (!b.label.trim()) return `Block ${i + 1} needs a name.`
    if (b.label.trim().length > MAX_TIMELINE_LABEL_LENGTH)
      return `Block ${i + 1} name is longer than ${MAX_TIMELINE_LABEL_LENGTH} characters.`
    if (!Number.isInteger(b.minutes) || b.minutes < 0 || b.minutes > MAX_TIMELINE_BLOCK_MINUTES)
      return `Block ${i + 1} must be between 0 and ${MAX_TIMELINE_BLOCK_MINUTES} minutes.`
  }
  return null
}

/** The update request's blocks, in draft order. */
export function draftToBlockInputs(draft: TimelineDraftBlock[]): EventTimelineBlockInput[] {
  return draft.map((b) =>
    create(EventTimelineBlockInputSchema, {
      id: b.id,
      kind: b.kind,
      label: b.label.trim(),
      start: timestampFromDate(b.start),
      minutes: b.minutes,
      note: b.note.trim(),
    }),
  )
}

/**
 * One line per changed input for the stale notice, e.g. "Carts changed from
 * 1 to 2". Changes with no single value ("Time settings changed") keep their
 * label.
 */
export function describeTimelineChanges(changes: EventPlanInputChange[]): string[] {
  return changes.map(describeInputChange)
}
